import React, { useState } from 'react';
import { CompetitionSearchQuery } from '../services/DataService';
import './AdvancedSearch.css';

interface AdvancedSearchProps {
  onSearch: (query: CompetitionSearchQuery) => void;
  isLoading: boolean;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  onSearch,
  isLoading
}) => {
  const [searchText, setSearchText] = useState('');
  const [tags, setTags] = useState('');
  const [dataDomain, setDataDomain] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minParticipants, setMinParticipants] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  
  // データドメインの選択肢（実際のアプリケーションではAPIから取得するなど）
  const domainOptions = [
    'computer vision',
    'natural language processing',
    'tabular data',
    'time series',
    'audio',
    'social science',
    'economics',
    'healthcare',
    'biology',
    'physics',
    'other'
  ];
  
  // タグの選択肢（実際のアプリケーションではAPIから取得するなど）
  const tagOptions = [
    'classification',
    'regression',
    'clustering',
    'recommendation',
    'forecasting',
    'image',
    'text',
    'audio',
    'video',
    'beginner',
    'intermediate',
    'advanced'
  ];
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const query: CompetitionSearchQuery = {
      text: searchText || undefined,
      tags: tags ? tags.split(',').map(tag => tag.trim()) : undefined,
      dataDomain: dataDomain || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      minParticipants: minParticipants ? parseInt(minParticipants, 10) : undefined,
      maxParticipants: maxParticipants ? parseInt(maxParticipants, 10) : undefined,
      isActive
    };
    
    onSearch(query);
  };
  
  const handleTagSelect = (tag: string) => {
    const currentTags = tags ? tags.split(',').map(t => t.trim()) : [];
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag];
      setTags(newTags.join(', '));
    }
  };
  
  const handleClearFilters = () => {
    setSearchText('');
    setTags('');
    setDataDomain('');
    setStartDate('');
    setEndDate('');
    setMinParticipants('');
    setMaxParticipants('');
    setIsActive(undefined);
  };
  
  return (
    <div className="advanced-search">
      <h2>高度な検索</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="searchText">検索テキスト:</label>
          <input
            type="text"
            id="searchText"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="タイトルやサブタイトルで検索"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="dataDomain">データドメイン:</label>
          <select
            id="dataDomain"
            value={dataDomain}
            onChange={(e) => setDataDomain(e.target.value)}
          >
            <option value="">すべてのドメイン</option>
            {domainOptions.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="tags">タグ:</label>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="カンマ区切りでタグを入力"
          />
          <div className="tag-suggestions">
            {tagOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                className="tag-button"
                onClick={() => handleTagSelect(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">開始日:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">終了日:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="minParticipants">最小参加者数:</label>
            <input
              type="number"
              id="minParticipants"
              value={minParticipants}
              onChange={(e) => setMinParticipants(e.target.value)}
              placeholder="例: 1000"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="maxParticipants">最大参加者数:</label>
            <input
              type="number"
              id="maxParticipants"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              placeholder="例: 10000"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label>ステータス:</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="isActive"
                checked={isActive === undefined}
                onChange={() => setIsActive(undefined)}
              />
              すべて
            </label>
            
            <label>
              <input
                type="radio"
                name="isActive"
                checked={isActive === true}
                onChange={() => setIsActive(true)}
              />
              アクティブなコンペのみ
            </label>
            
            <label>
              <input
                type="radio"
                name="isActive"
                checked={isActive === false}
                onChange={() => setIsActive(false)}
              />
              終了したコンペのみ
            </label>
          </div>
        </div>
        
        <div className="button-group">
          <button 
            type="submit" 
            className="search-button"
            disabled={isLoading}
          >
            {isLoading ? '検索中...' : '検索'}
          </button>
          
          <button 
            type="button" 
            className="clear-button"
            onClick={handleClearFilters}
          >
            フィルターをクリア
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdvancedSearch;
